import { action, computed, observable, makeObservable } from 'mobx';
import { anyMatch, allMatch, Matcher } from '../../../../../commonv3/utils/predicates';
import { EventIndexView } from '../../../../types/events';
import { RemoteEventListState } from './RemoteEventListState';
import { getLocationFilter, LocationFilter } from '../../../molecules/forms/LocationSelector';

export const CATEGORY_TAGS = ['ゲストイベント', 'もくもく会', '発表会', '交流イベント', 'その他'] as const;
export type CategoryTags = typeof CATEGORY_TAGS[number];
export const ATTRIBUTE_TAGS = ['初心者歓迎', 'シーメイトさん登壇', '土日祝日開催', '夜開催', '朝昼開催'] as const;
export type AttributeTags = typeof ATTRIBUTE_TAGS[number];

type UiFlags = {
  eventsMore: boolean;
  eventsSelectorOpen: boolean;
  eventArchivesMore: boolean;
  eventArchivesSelectorOpen: boolean;
};

export class EventListState {
  selectedLocationIds: number[] = [];
  selectedLocationSelectors: LocationFilter[] = [];
  selectedCategoryTagSelectors: CategoryTags[] | null = null;
  selectedAttributeTagSelectors: AttributeTags[] | null = null;
  uiFlags: UiFlags = {
    eventsMore: false,
    eventsSelectorOpen: false,
    eventArchivesMore: false,
    eventArchivesSelectorOpen: false,
  };
  private eventList: RemoteEventListState = RemoteEventListState.getInstance();

  constructor() {
    makeObservable<EventListState, 'filters'>(this, {
      selectedLocationIds: observable,
      selectedLocationSelectors: observable,
      selectedCategoryTagSelectors: observable,
      selectedAttributeTagSelectors: observable,
      uiFlags: observable,
      setSelectedLocationIds: action.bound,
      setSelectedLocationSelectors: action.bound,
      setSelectedCategoryTagSelectors: action.bound,
      setSelectedAttributeTagSelectors: action.bound,
      toggleUiFlag: action.bound,
      filteredEvents: computed,
      filteredEventArchives: computed,
      loading: computed,
      filters: computed,
    });
  }

  async init() {
    this.eventList.init();
  }

  setSelectedLocationIds(locationIds: Array<number>) {
    this.selectedLocationIds = locationIds;
  }

  setSelectedLocationSelectors(locationSelectors: LocationFilter[]) {
    this.selectedLocationSelectors = locationSelectors;
  }

  setSelectedCategoryTagSelectors(categoryTags: CategoryTags[]) {
    this.selectedCategoryTagSelectors = categoryTags;
  }

  setSelectedAttributeTagSelectors(attributeTags: AttributeTags[]) {
    this.selectedAttributeTagSelectors = attributeTags;
  }

  toggleUiFlag(key: keyof UiFlags) {
    this.uiFlags[key] = !this.uiFlags[key];
  }

  get filteredEvents() {
    return this.filters.reduce((e, f) => e.filter(f), this.eventList.events.getOrElse([]));
  }

  get filteredEventArchives() {
    return this.filters.reduce((e, f) => e.filter(f), this.eventList.eventArchives.getOrElse([]));
  }

  get loading() {
    return this.eventList.events.loading || this.eventList.eventArchives.loading;
  }

  private get filters() {
    // 会場による絞り込み
    const filters: Matcher<EventIndexView>[] = [
      anyMatch(this.selectedLocationSelectors.map((s) => getLocationFilter(s))),
    ];
    // カテゴリによる絞り込み
    if (this.selectedCategoryTagSelectors) {
      filters.push(anyMatch(this.selectedCategoryTagSelectors.map((s) => (l) => l.tag_list.includes(s))));
    }
    // こだわり条件による絞り込み（何も選択されていない場合はすべてのイベントを返すためにフィルタしない）
    if (this.selectedAttributeTagSelectors !== null && this.selectedAttributeTagSelectors.length > 0) {
      filters.push(allMatch(this.selectedAttributeTagSelectors.map((s) => (l) => l.tag_list.includes(s))));
    }
    return filters;
  }
}
